import axios from 'axios'
import get from 'lodash.get'
import * as Sentry from '@sentry/browser'

import { getBasket } from '@/src/utils/tableServiceGateway'
import router from '@router'

export const state = {
  brandId: null,
  brandName: null,
  location: {},
  tableNumber: null,
  accountId: null,
  basketId: null,
  billItems: [],
  billQuantity: null,
  braintreeToken: null,
  discount: null,
  emailAddress: null,
  gratuityAmount: null,
  paymentResult: null,
  processingPayment: false,
  tableServiceGateway: process.env.VUE_APP_PMB_GATEWAY_URL,
  // tableServiceGateway: 'http://localhost:3001',
  total: null,
  totalIncludingDiscount: null,
  totalIncludingDiscountAndGratuity: null,
}

export const getters = {
  accountId: (state) => {
    return state.accountId
  },
  basketId: (state) => {
    return state.basketId
  },
  billItems: (state) => {
    return state.billItems
  },
  billQuantity: (state) => {
    return state.billQuantity
  },
  braintreeToken: (state) => {
    return state.braintreeToken
  },
  brandId: (state) => {
    return state.brandId
  },
  brandName: (state) => {
    return state.brandName
  },
  discount: (state) => {
    return state.discount
  },
  emailAddress: (state) => {
    return state.emailAddress
  },
  gratuityAmount: (state) => {
    return state.gratuityAmount
  },
  location: (state) => {
    return get(state, 'location')
  },
  paymentResult: (state) => {
    return state.paymentResult
  },
  processingPayment: (state) => {
    return state.processingPayment
  },
  salesAreaId: (state) => {
    return get(state, 'location.fields.salesAreaId')
  },
  siteId: (state) => {
    return get(state, 'location.fields.siteId')
  },
  surveyLink: (state) => {
    return get(state, 'location.fields.surveyLink')
  },
  trgSiteId: (state) => {
    return get(state, 'location.fields.trgSiteId')
  },
  tableNumber: (state) => {
    return state.tableNumber
  },
  total: (state) => {
    return state.total
  },
  totalIncludingDiscount: (state) => {
    return state && state.totalIncludingDiscount
  },
  totalIncludingDiscountAndGratuity: (state) => {
    return state && state.totalIncludingDiscountAndGratuity
  },
}

export const mutations = {
  resetPaymentData(state) {
    state.accountId = null
    state.basketId = null
    state.billItems = []
    state.billQuantity = null
    state.braintreeToken = null
    state.discount = null
    state.emailAddress = null
    state.gratuityAmount = null
    state.location = {}
    state.paymentResult = null
    state.tableNumber = null
    state.total = null
    state.totalIncludingDiscount = null
    // TODO - add the rest
  },
  setAccountId(state, accountId) {
    state.accountId = accountId
  },
  setBasketId(state, basketId) {
    state.basketId = basketId
  },
  setBillItems(state, billItems) {
    state.billItems = billItems
  },
  setBillQuantity(state, billQuantity) {
    state.billQuantity = billQuantity
  },
  setBraintreeToken(state, braintreeToken) {
    state.braintreeToken = braintreeToken
  },
  setBrandId(state, brandId) {
    state.brandId = brandId
  },
  setBrandName(state, brandName) {
    state.brandName = brandName
  },
  setDiscount(state, discount) {
    state.discount = discount
  },
  setEmailAddress(state, { emailAddress }) {
    state.emailAddress = emailAddress
  },
  setGratuityAmount(state, { gratuityAmount }) {
    state.gratuityAmount = gratuityAmount
  },
  setLocation(state, location) {
    state.location = location
  },
  setPaymentProcessStatus(state, { processing }) {
    state.processingPayment = processing
  },
  setPaymentResult(state, { paymentResult }) {
    state.paymentResult = paymentResult
  },
  setTableNumber(state, tableNumber) {
    state.tableNumber = tableNumber
  },
  setTotal(state, total) {
    state.total = total
  },
  setTotalIncludingDiscount(state, totalIncludingDiscount) {
    state.totalIncludingDiscount = totalIncludingDiscount
  },
  setTotalIncludingDiscountAndGratuity(
    state,
    { totalIncludingDiscountAndGratuity }
  ) {
    state.totalIncludingDiscountAndGratuity = totalIncludingDiscountAndGratuity
  },
  setVoucherApplied(state, voucherApplied) {
    state.voucherApplied = voucherApplied
  },
}

export const actions = {
  async getBasketAndHandleResponse({ dispatch, getters }) {
    const siteId = getters.siteId
    const salesAreaId = getters.salesAreaId
    const tableNumber = getters.tableNumber

    try {
      const res = await getBasket({
        siteId,
        salesAreaId,
        tableNumber,
      })
      await dispatch('handleGetBasketResponse', { res })

      // TODO: CHECK IF WE NEED THIS:
      // commit('setBasketIsLoading', { basketIsLoading: false })

      return { success: true }
    } catch (err) {
      console.log(err)
      const slug = get(router, 'history.current.params.slug')
      if (slug) {
        dispatch(
          'ui/showToast',
          {
            toastMessage: `No account details were found for table number ${tableNumber}.`,
            limit: true,
          },
          { root: true }
        )
        return router.replace({ path: `/${slug}` })
      }
    }
  },
  handleGetBasketResponse({ commit }, { res }) {
    const {
      accountId,
      basketId,
      billItems,
      billQuantity,
      braintreeToken,
      discount,
      total,
      totalIncludingDiscount,
    } = res
    const voucherApplied = discount !== 0

    commit('setAccountId', accountId)
    commit('setBasketId', basketId)
    commit('setBillItems', billItems)
    commit('setTotal', total)
    commit('setTotalIncludingDiscount', totalIncludingDiscount)
    // TODO: CHECK IF WE NEED THIS:
    // commit('setTotalIncludingDiscountAndGratuity', {
    //   totalIncludingDiscountAndGratuity: parseFloat(
    //     totalIncludingDiscount || 0
    //   ).toFixed(2),
    // })

    commit('setVoucherApplied', voucherApplied)
    commit('setDiscount', discount)
    commit('setBillQuantity', billQuantity)
    commit('setBraintreeToken', braintreeToken)
  },
  resetPaymentData({ commit }) {
    commit('resetPaymentData')
  },
  setEmailAddress({ commit }, { emailAddress }) {
    commit('setEmailAddress', { emailAddress })
  },
  setGratuityAmount({ commit, getters }, { gratuityAmount }) {
    commit('setGratuityAmount', { gratuityAmount })
    const totalIncludingDiscountAndGratuity = (
      parseFloat(gratuityAmount || 0) +
      parseFloat(getters.totalIncludingDiscount || 0)
    ).toFixed(2)
    commit('setTotalIncludingDiscountAndGratuity', {
      totalIncludingDiscountAndGratuity,
    })
  },
  setLocation({ commit }, location) {
    commit('setLocation', location)
  },
  setTableNumber({ commit }, tableNumber) {
    commit('setTableNumber', tableNumber)
  },
  async processPayment({ commit, getters, state }, { nonce }) {
    const siteId = getters.siteId
    const trgSiteId = getters.trgSiteId
    const salesId = getters.salesAreaId
    const accountId = getters.accountId
    const basketId = getters.basketId
    const url = `${
      state.tableServiceGateway
    }/site/${siteId}/area/${salesId}/account/${accountId}/basket/${basketId}/pay`

    commit('setPaymentProcessStatus', { processing: true })

    try {
      if (!siteId) {
        throw new Error('siteId not defined')
      }
      if (!salesId) {
        throw new Error('salesId not defined')
      }
      if (!accountId) {
        throw new Error('accountId not defined')
      }
      if (!basketId) {
        throw new Error('basketId not defined')
      }

      const amount = getters.totalIncludingDiscount
      const customerEmail = getters.emailAddress
      const tableNumber = getters.tableNumber
      const brandId = getters.brandId
      const itemTotal = getters.total
      const location = getters.location
      const siteName = location && location.fields.name
      const discount = getters.discount
      const tip = getters.gratuityAmount
      const concessionName = getters.brandName
      const concessionSiteUrl = window.location.origin
      const appVersion = 'cpat'

      const res = await axios.post(url, {
        amount,
        appVersion,
        brandId,
        concessionName,
        concessionSiteUrl,
        customerEmail,
        discount,
        itemTotal,
        location,
        nonce,
        siteName,
        tableNumber,
        tip,
        trgSiteId,
      })

      if (!res.data) {
        commit('setPaymentResult', { paymentResult: 'failure' })
        commit('setPaymentProcessStatus', { processing: false })
        router.push('/failure')
        return
      }
      commit('setPaymentResult', { paymentResult: 'success' })
      commit('setPaymentProcessStatus', { processing: false })
      router.push('/success')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err)

      Sentry.withScope((scope) => {
        scope.setTag('processPayment')
        Sentry.captureException(err)
      })

      commit('setPaymentResult', { paymentResult: 'failure' })
      commit('setPaymentProcessStatus', { processing: false })
      router.push('/failure')
    }
  },
}
